import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Copy, ChevronLeft, ChevronRight, Eye, Trash2 } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce.js';
import { api, ApiError, Client, ClientFilters, Transaction } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import Modal, { ConfirmModal } from '../../components/ui/Modal';
import { formatAmount } from '../../lib/format';

export default function ClientsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { t: tt } = useTranslation('transactions');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ClientFilters>({
    page: 1,
    limit: 50,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteClient, setDeleteClient] = useState<Client | null>(null);
  const [viewTransactionsClient, setViewTransactionsClient] = useState<Client | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-clients', filters, debouncedSearch],
    queryFn: () =>
      api.admin.clients.list({
        ...filters,
        search: debouncedSearch?.trim() ? debouncedSearch.trim().toLowerCase() : undefined,
      }),
  });

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success(tc('actions.copied', 'Copied to clipboard'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const deleteMutation = useMutation({
    mutationFn: (client: Client) => api.clients.delete(client.entity!.id, client.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      toast.success(t('clients.deleteSuccess', 'Client deleted'));
      setDeleteClient(null);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  const toggleEmployeeMutation = useMutation({
    mutationFn: (client: Client) =>
      api.clients.update(client.entity!.id, client.id, { isEmployee: !client.isEmployee }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  // Fetch transactions for the selected client
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['client-transactions', viewTransactionsClient?.id, viewTransactionsClient?.entity?.id],
    queryFn: () =>
      api.transactions.list(viewTransactionsClient!.entity!.id, {
        clientId: viewTransactionsClient!.id,
        limit: 100,
      }),
    enabled: !!viewTransactionsClient?.entity?.id,
  });

  const handleViewTransactions = (client: Client) => {
    setViewTransactionsClient(client);
  };

  const clients: Client[] = data?.data || [];
  const totalPages = data?.meta?.totalPages || 1;
  const currentPage = filters.page || 1;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('clients.title', 'Clients')}</h1>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          className="form-input !w-auto min-w-[220px]"
          placeholder={t('clients.searchPlaceholder', 'Search by name...')}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setFilters({ ...filters, page: 1 });
          }}
        />
      </div>

      {isLoading ? (
        <p className="text-muted">{tc('actions.loading')}</p>
      ) : error ? (
        <p className="text-error">{tc('common.error', 'An error occurred')}</p>
      ) : (
        <div className="card">
          {clients.length === 0 ? (
            <div className="py-8 text-center text-muted">
              {searchQuery ? t('clients.empty.description', 'Try a different search term') : t('clients.empty.description', 'Clients will appear here after transactions are created')}
            </div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('clients.table.id', 'ID')}</th>
                    <th>{t('clients.table.clientName', 'CLIENT NAME')}</th>
                    <th>{t('clients.table.entity', 'ENTITY')}</th>
                    <th className="text-center">{t('clients.table.employee', 'EMPLOYEE')}</th>
                    <th className="text-right">{t('clients.table.transactions', 'TRANSACTIONS')}</th>
                    <th>{t('clients.table.regDate', 'FIRST SEEN')}</th>
                    <th>{t('clients.table.actions', 'ACTIONS')}</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs" title={client.id}>
                            {client.id.slice(0, 8)}...
                          </span>
                          <button
                            className={`text-muted hover:text-foreground ${copiedId === client.id ? 'text-success' : ''}`}
                            onClick={() => handleCopyId(client.id)}
                            title={tc('actions.copy', 'Copy ID')}
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{client.name}</strong>
                          {client.uniqueId && (
                            <code className="text-xs text-foreground-muted bg-surface-hover px-1.5 py-0.5 rounded mt-1 block w-fit">{client.uniqueId}</code>
                          )}
                        </div>
                      </td>
                      <td>
                        {client.entity ? (
                          <div>
                            <strong>{client.entity.code}</strong>
                            <div className="text-xs text-muted">{client.entity.name}</div>
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={!!client.isEmployee}
                          disabled={toggleEmployeeMutation.isPending}
                          onChange={() => toggleEmployeeMutation.mutate(client)}
                          title={t('clients.actions.toggleEmployee', 'Toggle employee status')}
                        />
                      </td>
                      <td className="text-right">{client.transactionCount || 0}</td>
                      <td className="text-muted">{formatDate(client.createdAt)}</td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-secondary px-2 py-1 text-[13px]"
                            onClick={() => handleViewTransactions(client)}
                            title={t('clients.actions.viewTransactions', 'View Transactions')}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            className="btn btn-danger px-2 py-1 text-[13px]"
                            onClick={() => setDeleteClient(client)}
                            disabled={!!client.transactionCount && client.transactionCount > 0}
                            title={
                              client.transactionCount && client.transactionCount > 0
                                ? t('clients.deleteDisabled', 'Cannot delete client with transactions')
                                : t('clients.actions.delete', 'Delete')
                            }
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center px-4 py-3 border-t border-border">
                  <span className="text-sm text-muted">
                    {t('clients.page', 'Page')} {currentPage} {t('clients.of', 'of')} {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={currentPage === 1}
                      onClick={() => setFilters({ ...filters, page: currentPage - 1 })}
                    >
                      <ChevronLeft size={14} />
                      {tc('actions.previous', 'Previous')}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setFilters({ ...filters, page: currentPage + 1 })}
                    >
                      {tc('actions.next', 'Next')}
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteClient}
        onClose={() => setDeleteClient(null)}
        onConfirm={() => deleteClient && deleteMutation.mutate(deleteClient)}
        title={t('clients.deleteConfirm.title', 'Delete Client')}
        message={t('clients.deleteConfirm.message', `Are you sure you want to delete "${deleteClient?.name}"? This action cannot be undone.`)}
        confirmText={tc('actions.delete', 'Delete')}
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Transactions Modal */}
      <Modal
        isOpen={!!viewTransactionsClient}
        onClose={() => setViewTransactionsClient(null)}
        title={`${t('clients.transactionsModal.title', 'Transactions')} - ${viewTransactionsClient?.name || ''}`}
        size="lg"
      >
        {transactionsLoading ? (
          <div className="py-8 text-center text-muted">{tc('actions.loading')}</div>
        ) : transactionsData?.data && transactionsData.data.length > 0 ? (
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="table text-sm">
              <thead className="sticky top-0 bg-surface">
                <tr>
                  <th>{tt('fields.id', 'ID')}</th>
                  <th>{tt('fields.type', 'Type')}</th>
                  <th className="text-right">{tt('fields.amountIn', 'Amount In')}</th>
                  <th className="text-right">{tt('fields.amountOut', 'Amount Out')}</th>
                  <th>{tt('fields.status', 'Status')}</th>
                  <th>{tt('fields.date', 'Date')}</th>
                </tr>
              </thead>
              <tbody>
                {transactionsData.data.map((tx: Transaction) => {
                  const isExchange = tx.type === 'exchange';
                  const isInflow = tx.type === 'deposit' || tx.type === 'transfer';
                  return (
                    <tr key={tx.id}>
                      <td>
                        <Link
                          to={`/transactions/${tx.id}`}
                          className="font-mono text-xs text-primary hover:underline"
                          onClick={() => setViewTransactionsClient(null)}
                        >
                          {tx.id.slice(0, 8)}
                        </Link>
                      </td>
                      <td>
                        <span className={`badge badge-${tx.type}`}>
                          {tt(`types.${tx.type}`, tx.type)}
                        </span>
                      </td>
                      <td className="text-right">
                        {isExchange
                          ? formatAmount(tx.amountIn, tx.currencyIn)
                          : isInflow
                            ? formatAmount(tx.amount, tx.currency)
                            : '-'}
                      </td>
                      <td className="text-right">
                        {isExchange
                          ? formatAmount(tx.amountOut, tx.currencyOut)
                          : !isInflow
                            ? formatAmount(tx.amount, tx.currency)
                            : '-'}
                      </td>
                      <td>
                        <span className={`badge ${tx.status === 'open' ? 'badge-open' : 'badge-closed'}`}>
                          {tt(`status.${tx.status}`, tx.status)}
                        </span>
                      </td>
                      <td className="text-muted">{formatDate(tx.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-muted">
            {t('clients.transactionsModal.empty', 'No transactions found for this client')}
          </div>
        )}
      </Modal>
    </div>
  );
}
